import { TFormalOffer } from '@helpers/FormalOffers/formalOffers.types';
import { getFormalOffersMaxInterestOnlyPeriod } from '@helpers/FormalOffers/getFormalOffersMaxInterestOnlyPeriod';
import { Radio } from '@iwoca/orion';
import { FormattedMessage } from 'react-intl';

import messages from './selectOfferType.messages';
import { useGetSelectOfferTypeData } from './useGetSelectOfferTypeData';

export const FlexibleAndInterestOnlyOffers = ({
    handleOptionSelected,
    isDrawdownAmountInRange,
    selectedOffer,
    flexibleOfferSelected
}: {
    handleOptionSelected: (offers: TFormalOffer[]) => void;
    isDrawdownAmountInRange: (offers: TFormalOffer[]) => boolean;
    selectedOffer: TFormalOffer | null | undefined;
    flexibleOfferSelected: boolean;
}) => {
    const { hasRCFOffer } = useGetSelectOfferTypeData();

    return (
        <>
            <p className="text-m font-bold">
                <FormattedMessage
                    id="_.offer_type_select.interest_only_offer_type_heading"
                    defaultMessage="I want my offer terms to be"
                />
            </p>
            <div className="flex flex-col gap-m">
                <FlexibleOfferRadioButton
                    handleOptionSelected={handleOptionSelected}
                    isDrawdownAmountInRange={isDrawdownAmountInRange}
                    flexibleOfferSelected={flexibleOfferSelected}
                />
                {!hasRCFOffer ? (
                    <InterestOnlyOfferRadioButton
                        handleOptionSelected={handleOptionSelected}
                        isDrawdownAmountInRange={isDrawdownAmountInRange}
                        selectedOffer={selectedOffer}
                    />
                ) : null}
            </div>
        </>
    );
};

const FlexibleOfferRadioButton = ({
    handleOptionSelected,
    isDrawdownAmountInRange,
    flexibleOfferSelected
}: {
    handleOptionSelected: (offers: TFormalOffer[]) => void;
    isDrawdownAmountInRange: (offers: TFormalOffer[]) => boolean;
    flexibleOfferSelected: boolean;
}) => {
    const { flexibleOffers } = useGetSelectOfferTypeData();

    return (
        <Radio
            frame
            id="flexible-loans-offers"
            label={
                (
                    <p className="w-10/12 font-reg">
                        <FormattedMessage
                            tagName="span"
                            {...messages.flexibleLoansTitle_withIOoption}
                            values={{
                                strong: (chunks) => <strong>{chunks}</strong>
                            }}
                        />
                    </p>
                ) as unknown as string
            }
            name="offer-selection-buttons"
            onChange={() => handleOptionSelected(flexibleOffers)}
            disabled={isDrawdownAmountInRange(flexibleOffers)}
            checked={flexibleOfferSelected}
        />
    );
};

const InterestOnlyOfferRadioButton = ({
    selectedOffer,
    handleOptionSelected,
    isDrawdownAmountInRange
}: {
    selectedOffer: TFormalOffer | null | undefined;
    handleOptionSelected: (offers: TFormalOffer[]) => void;
    isDrawdownAmountInRange: (offers: TFormalOffer[]) => boolean;
}) => {
    const { interestOnlyOffers, shouldUseInterestOnlyPlusCopy } =
        useGetSelectOfferTypeData();

    const interestOnlyOfferSelected = Boolean(
        interestOnlyOffers.find((offer) => offer === selectedOffer)
    );

    return (
        <Radio
            frame
            id="interest-only-loans-offers"
            label={
                (
                    <p className="w-10/12 font-reg">
                        {shouldUseInterestOnlyPlusCopy ? (
                            <FormattedMessage
                                tagName="span"
                                id="_.offer_type_select.interest_only_plus_loans_title"
                                defaultMessage="<strong>Flexible credit line:</strong> liquidity as needed"
                                values={{
                                    strong: (chunks) => (
                                        <strong>{chunks}</strong>
                                    )
                                }}
                            />
                        ) : (
                            <FormattedMessage
                                tagName="span"
                                id="_.offer_type_select.interest_only_loans_title"
                                defaultMessage="<strong>Interest Only:</strong> pay only interest for the first {maxInterestOnlyPeriod} months"
                                values={{
                                    strong: (chunks) => (
                                        <strong>{chunks}</strong>
                                    ),
                                    maxInterestOnlyPeriod:
                                        getFormalOffersMaxInterestOnlyPeriod(
                                            interestOnlyOffers
                                        )
                                }}
                            />
                        )}
                    </p>
                ) as unknown as string
            }
            name="offer-selection-buttons"
            onChange={() => handleOptionSelected(interestOnlyOffers)}
            disabled={isDrawdownAmountInRange(interestOnlyOffers)}
            checked={interestOnlyOfferSelected}
        />
    );
};
